import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import * as he from '@angular/common/locales/he';
import {
  EnvironmentProviders,
  Injectable,
  LOCALE_ID,
  enableProdMode,
  importProvidersFrom,
  isDevMode,
  makeEnvironmentProviders,
} from '@angular/core';
import { provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatIconModule } from '@angular/material/icon';
import { bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  withPreloading,
} from '@angular/router';
import { devTools } from '@ngneat/elf-devtools';
import {
  Translation,
  TranslocoLoader,
  provideTransloco,
} from '@ngneat/transloco';
import { provideTranslocoLocale } from '@ngneat/transloco-locale';
import { provideUserIdleConfig } from 'angular-user-idle';
import { getTimezoneOffset } from 'date-fns-tz';
import { initializeApp } from 'firebase/app';
import player from 'lottie-web';
import {
  LoggerModule,
  NgxLoggerLevel,
  TOKEN_LOGGER_SERVER_SERVICE,
  TOKEN_LOGGER_WRITER_SERVICE,
} from 'ngx-logger';
import { LottieModule } from 'ngx-lottie';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { ElfJsWriterStoreService } from './app/logger/custom.writer';
import { AuthTokenServerService } from './app/services/authToken.service';
import { environment } from './environments/environment';

registerLocaleData(he.default);
devTools();

// Note we need a separate function as it's required
// by the AOT compiler.
export function playerFactory() {
  return player;
}

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  constructor(private http: HttpClient) {}

  getTranslation(lang: string) {
    return this.http.get<Translation>(`/assets/i18n/${lang}.json`);
  }
}

export function provideTranslocoInner(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideTransloco({
      config: {
        availableLangs: ['en', 'he'],
        defaultLang: 'he',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    //provideTranslocoPreloadLangs(['he'])
  ]);
}

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: {
        timezone: `+0${
          getTimezoneOffset('Asia/Jerusalem') / (1000 * 60 * 60)
        }00`,
      },
    },
    provideTranslocoInner(),
    provideTranslocoLocale({
      langToLocaleMapping: { en: 'en-US', he: 'he-IL' },
    }),
    provideUserIdleConfig({ idle: 180, timeout: 45, ping: 0 }),
    importProvidersFrom(
      [HttpClientModule],
      BrowserAnimationsModule,
      FlexLayoutModule,
      MatIconModule,
      LottieModule.forRoot({ player: playerFactory }),
      LoggerModule.forRoot(
        {
          serverLoggingUrl: environment.apiUrl + '/insert-log',
          serverCallsBatchSize: 1,
          serverCallsTimer: 5000,
          level: NgxLoggerLevel.DEBUG,
          serverLogLevel: NgxLoggerLevel.DEBUG,
        },
        {
          writerProvider: {
            provide: TOKEN_LOGGER_WRITER_SERVICE,
            useClass: ElfJsWriterStoreService,
          },
          serverProvider: {
            provide: TOKEN_LOGGER_SERVER_SERVICE,
            useClass: AuthTokenServerService,
          },
        }
      )
    ),
    importProvidersFrom(
      provideFirebaseApp(() => initializeApp(environment.firebase))
    ),
    importProvidersFrom(provideAuth(() => getAuth())),
    importProvidersFrom(provideFirestore(() => getFirestore())),
    importProvidersFrom(provideStorage(() => getStorage())),
    importProvidersFrom(providePerformance(() => getPerformance())),
    { provide: LOCALE_ID, useValue: 'en-GB' },
    provideRouter(routes, withPreloading(PreloadAllModules)),
  ],
});
